import api from '../../../../helpers/api_helper';
import type {
  PassbookItem,
  PassbookResponse,
  PlayerDepositResponse,
  PlayerOverview,
} from '../types';

export const fetchPlayerOverview = async (
  playerId: string | number,
): Promise<PlayerOverview> => {
  const response = await api.get(`/players/${playerId}/overview/`);

  if (!response.data?.success || !response.data?.data) {
    throw new Error(
      response.data?.error || 'Failed to load player overview.',
    );
  }

  return response.data.data;
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const fetchPlayerPassbook = async (
  playerId: string | number,
  page = 1,
  pageSize = 20,
  transactionType = 'all',
  search = '',
  dateFrom = '',
  dateTo = '',
): Promise<PassbookResponse> => {
  const params = new URLSearchParams();

  params.set('page', String(page));
  params.set('page_size', String(pageSize));

  // Normal admin passbook shows economically effective history.
  // Fully reversed market settlements and their matching reversal
  // journals remain preserved in the backend audit ledger, but are
  // omitted from this simplified wallet-history view.
  params.set('effective_only', 'true');

  if (transactionType && transactionType !== 'all') {
    params.set('transaction_type', transactionType);
  }

  if (dateFrom) {
    params.set('date_from', dateFrom);
  }

  if (dateTo) {
    params.set('date_to', dateTo);
  }

  const response = await api.get(
    `/players/${playerId}/ledger/?${params.toString()}`,
  );

  if (!response.data?.success || !response.data?.data) {
    throw new Error(
      response.data?.error || 'Failed to load player ledger.',
    );
  }

  const ledger = response.data.data;

  let items: PassbookItem[] = (ledger.results || []).map(
    (entry: any): PassbookItem => {
      const bet = entry.metadata?.bet || null;
      const starlineBet = entry.metadata?.starline_bet || null;
      const sourceBet = bet || starlineBet;

      const placedBets =
        bet?.original_payload?.bets ||
        (starlineBet
          ? [
              {
                pana: starlineBet.selected_number,
                digit: starlineBet.selected_number,
                combination: starlineBet.selected_number,
                points: starlineBet.amount,
              },
            ]
          : []);

      return {
        id: entry.id,
        type:
          entry.summary?.source === 'starline'
            ? 'bet'
            : entry.summary?.source === 'market_bet'
              ? 'bet'
              : entry.transaction_type,
        type_display:
          entry.summary?.title || entry.transaction_type,
        transaction_type: entry.transaction_type,
        market: sourceBet
          ? {
              id: sourceBet.market_id,
              name: sourceBet.market_name,
            }
          : null,
        bet_type: sourceBet
          ? {
              id: sourceBet.bet_type_id,
              name: sourceBet.bet_type_name,
            }
          : null,
        app_bet_type: sourceBet
          ? {
              id: sourceBet.bet_type_id,
              payout_multiplier: toNumber(
                starlineBet?.payout_multiplier,
              ),
            }
          : null,
        bet_details: sourceBet
          ? {
              session:
                bet?.bet_session ||
                starlineBet?.session ||
                null,
              bet_type:
                bet?.bet_session ||
                starlineBet?.bet_code ||
                null,
              open_pana: bet?.open_pana,
              open_digit: bet?.open_digit,
              close_pana: bet?.close_pana,
              close_digit: bet?.close_digit,
              placed_bets: placedBets,
              result_data: bet
                ? {
                    original_payload: bet.original_payload,
                    processed_result: bet.processed_result,
                  }
                : {
                    original_payload: {
                      bets: placedBets,
                    },
                    processed_result: {},
                  },
            }
          : null,
        amount: toNumber(entry.amount_change),
        bet_amount: toNumber(sourceBet?.amount),
        win_amount: toNumber(
          bet?.potential_payout ||
            starlineBet?.winning_amount ||
            starlineBet?.potential_payout,
        ),
        before_amount:
          entry.previous_amount == null
            ? undefined
            : toNumber(entry.previous_amount),
        after_amount:
          entry.current_amount == null
            ? undefined
            : toNumber(entry.current_amount),
        // Ledger rows are historical financial events.
        // Their displayed status must describe the journal entry itself,
        // not the related bet's current status after later reconciliation.
        status:
          entry.transaction_type === 'bet_won'
            ? 'won'
            : entry.transaction_type === 'bet_lost'
              ? 'lost'
              : entry.transaction_type === 'reconciliation'
                ? 'reconciled'
                : entry.transaction_type === 'bet_placed'
                  ? 'placed'
                  : entry.status || 'completed',
        status_display:
          entry.transaction_type === 'bet_won'
            ? (
                entry.metadata?.reversed
                  ? 'Won · Reversed'
                  : 'Won'
              )
            : entry.transaction_type === 'bet_lost'
              ? (
                  entry.metadata?.reversed
                    ? 'Lost · Reversed'
                    : 'Lost'
                )
              : entry.transaction_type === 'reconciliation'
                ? 'Reconciled'
                : entry.transaction_type === 'bet_placed'
                  ? 'Placed'
                  : entry.status || 'Completed',
        description:
          entry.particulars ||
          entry.summary?.title ||
          'Transaction',
        gateway: null,
        transaction_id: entry.reference,
        created_at: entry.datetime,
        updated_at: entry.datetime,
        ledger_summary: entry.summary,
        ledger_metadata: entry.metadata,
      } as PassbookItem;
    },
  );

  if (search.trim()) {
    const normalizedSearch = search.trim().toLowerCase();

    items = items.filter((item) =>
      [
        item.description,
        item.type_display,
        item.transaction_id,
        item.market?.name,
        item.bet_type?.name,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }

  const totalCount = Number(ledger.count || 0);
  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / Number(ledger.page_size || pageSize)),
  );

  if (!ledger.summary) {
    throw new Error(
      'Player ledger summary is missing from the API response.',
    );
  }

  return {
    success: true,
    data: items,
    summary: ledger.summary,
    pagination: {
      current_page: Number(ledger.page || page),
      total_pages: totalPages,
      total_count: totalCount,
      has_next: Boolean(ledger.has_next),
      has_previous: Boolean(ledger.has_previous),
      page_size: Number(ledger.page_size || pageSize),
    },
  };
};

export const fetchPlayerDeposits = async (
  playerId: string | number,
  page = 1,
  pageSize = 20,
  search = '',
  dateFrom = '',
  dateTo = '',
): Promise<PlayerDepositResponse> => {
  const params = new URLSearchParams();

  params.set('page', String(page));
  params.set('page_size', String(pageSize));

  if (search.trim()) {
    params.set('search', search.trim());
  }

  if (dateFrom) {
    params.set('date_from', dateFrom);
  }

  if (dateTo) {
    params.set('date_to', dateTo);
  }

  const response = await api.get(
    `/players/${playerId}/deposits/?${params.toString()}`,
  );

  if (!response.data?.success || !response.data?.data) {
    throw new Error(
      response.data?.error || 'Failed to load player deposit history.',
    );
  }

  const depositData = response.data.data;

  const items: PassbookItem[] = (depositData.results || []).map(
    (entry: any): PassbookItem => ({
      id: String(entry.id),
      type: entry.type || 'deposit',
      type_display: entry.description || 'Deposit',
      transaction_type: 'deposit',
      amount: toNumber(entry.amount),
      status: entry.status || 'pending',
      status_display:
        entry.status_display ||
        entry.status ||
        'Pending',
      description: entry.description || 'Deposit',
      gateway: entry.gateway || null,
      transaction_id:
        entry.utr ||
        entry.gateway_txn_id ||
        entry.gateway_order_id ||
        entry.transaction_id ||
        String(entry.id),
      created_at: entry.created_at,
      updated_at: entry.updated_at,
      ledger_metadata: {
        payment_id:
          entry.source_type === 'gateway'
            ? Number(
                String(entry.id).replace('payment-', ''),
              ) || null
            : null,
        actor: entry.actor || entry.reviewer || null,
        source_label: entry.source_type || null,
        method_label: entry.gateway || null,
      },
    }));

  const totalCount = Number(depositData.count || 0);
  const pageSizeValue = Number(
    depositData.page_size || pageSize,
  );

  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / pageSizeValue),
  );

  return {
    success: true,
    data: items,
    summary: {
      total_amount: toNumber(
        depositData.summary?.total_amount,
      ),
      successful_amount: toNumber(
        depositData.summary?.successful_amount,
      ),
      pending_amount: toNumber(
        depositData.summary?.pending_amount,
      ),
      rejected_amount: toNumber(
        depositData.summary?.rejected_amount,
      ),
      total_count: Number(
        depositData.summary?.total_count ||
        totalCount,
      ),
    },
    pagination: {
      current_page: Number(depositData.page || page),
      total_pages: totalPages,
      total_count: totalCount,
      has_next: Boolean(depositData.has_next),
      has_previous: Boolean(
        depositData.has_previous,
      ),
      page_size: pageSizeValue,
    },
  };
};

